<template>
  <div
    :ref="`card-media-${formattedDate}-${indexMedia}`"
    class="mc-vehicle-media__media-item-wrapper"
    @click="handleClickMediaItem"
  >
    <b-skeleton v-if="refreshThumbnail" height="211px" />
    <div v-else class="mc-vehicle-media__media-thumbnail-wrapper">
      <template v-if="isImage">
        <b-spinner v-if="isGeneratingThumbnail" small />
        <img
          v-else
          :src="calculatedSrcImage"
          class="mc-vehicle-media__media-thumbnail"
          loading="lazy"
          @error="handleThumbnailError"
        />
      </template>
      <template v-else-if="isVideo">
        <i
          v-if="!isGeneratingThumbnail"
          class="mc-icon icon-fill_play mc-vehicle-media__icon-play-video"
        />
        <b-spinner v-if="isGeneratingThumbnail" small />
        <img
          v-else
          :src="calculatedThumbnailSrc"
          alt="thumbnail-video-media"
          class="mc-vehicle-media__media-thumbnail"
          loading="lazy"
          @error="handleThumbnailError"
        />
      </template>
      <!-- TODO : Adjust when thumbnail video is ready -->
      <!-- <video v-else class="mc-vehicle-media__video" :src="srcMedia" /> -->
      <span class="mc-vehicle-media__label-camera">
        {{ labelCamera }}
      </span>
      <span v-if="isAlert" class="mc-vehicle-meida__notif-icon">
        <i class="mc-icon icon-fill_bell" :class="{ important: isImportantAlert }" />
      </span>
      <span
        v-if="groupingBasedOnDate && mediaGroup.mediaList.length > 1"
        class="mc-vehicle-media__label-num-capture"
      >
        <i class="mc-icon icon-outline-images" />
        <span> {{ mediaCount }} </span>
      </span>
    </div>
    <div class="mc-vehicle-media__media-item-content">
      <div class="mc-vehicle-media__media-first-row">
        <p
          v-b-tooltip.hover.right="$t('media.fieldsManager.name')"
          class="mc-vehicle-media__media-type"
        >
          {{ mediaTypeFormatted }}
        </p>
        <p v-b-tooltip.hover.right="$t('global.field.savedDate')">{{ mediaDate }}</p>
      </div>
      <div class="mc-vehicle-media__media-second-row">
        <h4 v-b-tooltip.hover.right="licensePlate">{{ licensePlate }}</h4>
        <p v-if="fieldsStatus.driver" v-b-tooltip.hover.right="driverName">
          {{ driverName }}
        </p>
      </div>
      <div class="mc-vehicle-media__media-item-last-section">
        <div v-if="fieldsStatus.vehicleType" class="mc-vehicle-media__description-item">
          <i class="mc-icon icon-outline_license-general" />
          <p v-b-tooltip.hover.right="vehicleType">
            {{ vehicleType }}
          </p>
        </div>
        <div v-if="fieldsStatus.vehicleGroup" class="mc-vehicle-media__description-item">
          <i class="mc-icon icon-outline_car-group" />
          <p v-b-tooltip.hover.right="vgroupsList.map((g) => g.vgroup.name).join(', ')">
            {{ vgroupsFormatted }}
          </p>
        </div>
        <div
          v-if="fieldsStatus.address"
          class="mc-vehicle-media__description-item address-data"
          :class="{ loading: !formatAddress }"
        >
          <i class="mc-icon icon-outline_pin" />
          <b-skeleton v-if="!formatAddress" />
          <p v-else v-b-tooltip.hover.right="formatAddress">
            {{ formatAddress }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { mapState } from 'vuex'

export default {
  mixins: [Helper],
  props: {
    mediaItem: {
      type: Object,
      required: false,
      default: null,
    },
    mediaGroup: {
      type: Object,
      required: false,
      default: null,
    },
    formattedDate: {
      type: String,
      required: true,
    },
    indexMedia: {
      type: Number,
      required: true,
    },
    address: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isThumbnailExist: true,
      generatedThumbnailSrc: null,
      isGeneratingThumbnail: false,
      refreshThumbnail: false,
    }
  },

  computed: {
    ...mapState('media', ['fieldsStatus', 'defaultCameraThumbnail', 'groupingBasedOnDate']),
    formatAddress() {
      return this.address
    },
    mediaBasedOnThumbnail() {
      if (!this.groupingBasedOnDate) return null
      const mediaPhoto = this.mediaGroup.mediaList.filter((m) => m.identifier === 'P')
      const mediaVideo = this.mediaGroup.mediaList.filter(
        (m) => m.identifier === 'V' || m.identifier === 'H' || m.identifier === 'R'
      )

      let mediaBasedOnThumbnail
      if (mediaPhoto.length) {
        mediaBasedOnThumbnail = mediaPhoto.find(
          (media) => media.calculatedChannel === this.defaultCameraThumbnail
        )
        if (!mediaBasedOnThumbnail) {
          mediaBasedOnThumbnail = mediaPhoto[0]
        }
      } else if (mediaVideo.length) {
        mediaBasedOnThumbnail = mediaVideo.find(
          (media) => media.calculatedChannel === this.defaultCameraThumbnail
        )
        if (!mediaBasedOnThumbnail) {
          mediaBasedOnThumbnail = mediaVideo[0]
        }
      }
      return mediaBasedOnThumbnail
    },
    srcMedia() {
      if (!this.groupingBasedOnDate) return this.mediaItem.url
      return this.mediaBasedOnThumbnail
        ? this.mediaBasedOnThumbnail.url
        : this.mediaGroup.mediaList[0].url
    },
    isPictureFromVideo() {
      if (!this.groupingBasedOnDate) return this.mediaItem?.isPictureFromVideo
      return this.mediaBasedOnThumbnail
        ? this.mediaBasedOnThumbnail?.isPictureFromVideo
        : this.mediaGroup.mediaList[0]?.isPictureFromVideo
    },
    labelCamera() {
      if (!this.groupingBasedOnDate)
        return this.$t('trackVideo.camera') + ' ' + this.mediaItem.calculatedChannel
      return (
        this.$t('trackVideo.camera') +
        ' ' +
        (this.mediaBasedOnThumbnail
          ? this.mediaBasedOnThumbnail.calculatedChannel
          : this.mediaGroup.mediaList[0].calculatedChannel)
      )
    },
    mediaDate() {
      if (!this.groupingBasedOnDate) {
        const tz = this.mediaItem.vehicle.device?.timezone ?? 7
        return (
          this.momentYmDHms(this.convertUtcToLocalDate(this.mediaItem.startTime, tz)) +
          ' ' +
          this.formatLabelTimezone(tz)
        )
      }
      const tz = this.mediaGroup.mediaList[0].vehicle.device?.timezone ?? 7
      return (
        this.momentYmDHms(this.convertUtcToLocalDate(this.mediaGroup.mediaList[0].startTime, tz)) +
        ' ' +
        this.formatLabelTimezone(tz)
      )
    },
    licensePlate() {
      if (!this.groupingBasedOnDate) {
        return this.mediaItem.vehicle.parentVehicle
          ? this.mediaItem.vehicle.parentVehicle?.licensePlate
          : this.mediaItem.vehicle.licensePlate
      }
      return this.mediaGroup.mediaList[0].vehicle.parentVehicle
        ? this.mediaGroup.mediaList[0].vehicle.parentVehicle?.licensePlate
        : this.mediaGroup.mediaList[0].vehicle.licensePlate
    },
    vehicleType() {
      let category, type, manufacturer
      if (!this.groupingBasedOnDate) {
        category = this.mediaItem.vehicle.vehicleType.category
        type = this.mediaItem.vehicle.vehicleType.type
        manufacturer = this.mediaItem.vehicle.vehicleType.manufacturer
      } else {
        category = this.mediaGroup.mediaList[0].vehicle.vehicleType.category
        type = this.mediaGroup.mediaList[0].vehicle.vehicleType.type
        manufacturer = this.mediaGroup.mediaList[0].vehicle.vehicleType.manufacturer
      }
      return (category ?? '-') + ' / ' + (type ?? '-') + ' / ' + (manufacturer ?? '-')
    },
    driverName() {
      if (!this.groupingBasedOnDate) {
        return this.mediaItem.vehicle.parentVehicle
          ? this.mediaItem.vehicle.parentVehicle?.driver1?.fullname ?? '-'
          : this.mediaItem.vehicle?.driver1?.fullname ?? '-'
      }
      return this.mediaGroup.mediaList[0].vehicle.parentVehicle
        ? this.mediaGroup.mediaList[0].vehicle.parentVehicle?.driver1?.fullname ?? '-'
        : this.mediaGroup.mediaList[0].vehicle?.driver1?.fullname ?? '-'
    },
    vgroupsList() {
      let vgroupList
      if (!this.groupingBasedOnDate) {
        vgroupList = this.mediaItem?.vehicle?.vehicle2Vgroup ?? []
      } else {
        vgroupList = this.mediaGroup.mediaList[0]?.vehicle?.vehicle2Vgroup ?? []
      }
      return vgroupList
    },
    vgroupsFormatted() {
      let vgroupList = this.vgroupsList
      if (vgroupList?.length === 0) return '-'
      if (vgroupList.length === 1) return vgroupList[0]?.vgroup?.name ?? '-'
      return (
        (vgroupList[0]?.vgroup?.name ?? '-') + ', +' + (vgroupList.length - 1) + ' grup lainnnya'
      )
    },
    getFormatUrl() {
      if (this.mediaItem.url) {
        const arrUrl = this.mediaItem.url.split('?')
        const arrFormat = arrUrl[0].split('.')
        const format = arrFormat[arrFormat.length - 1]
        return format.toLowerCase()
      }
      return ''
    },
    isImage() {
      if (!this.groupingBasedOnDate) return this.mediaItem.identifier === 'P'
      return this.mediaBasedOnThumbnail.identifier === 'P'
    },
    isVideo() {
      if (!this.groupingBasedOnDate)
        return (
          this.mediaItem.identifier === 'H' ||
          this.mediaItem.identifier === 'V' ||
          this.mediaItem.identifier === 'R'
        )
      return (
        this.mediaBasedOnThumbnail.identifier === 'H' ||
        this.mediaBasedOnThumbnail.identifier === 'V' ||
        this.mediaBasedOnThumbnail.identifier === 'R'
      )
    },
    isAlert() {
      if (!this.groupingBasedOnDate) return this.mediaItem.rawAlerts.length > 0
      const rawAlerts = this.mediaGroup.rawAlerts
      return rawAlerts.length > 0
    },
    isImportantAlert() {
      if (!this.isAlert) return false
      if (!this.groupingBasedOnDate) return this.mediaItem.rawAlerts[0].isImportant
      const rawAlerts = this.mediaGroup.rawAlerts
      return rawAlerts[0].isImportant
    },
    mediaTypeFormatted() {
      const lang = this.$i18n.locale
      if (!this.groupingBasedOnDate) {
        if (this.mediaItem.identifier === 'H') {
          return 'Download Riwayat Video'
        }
        if (this.mediaItem.rawAlerts.length) {
          if (lang === 'en') {
            return this.mediaItem.rawAlerts[0].alert.descriptionEng
          }
          return this.mediaItem.rawAlerts[0].alert.descriptionIdn
        }
        return 'Auto Captured'
      }
      const mapKey = {
        autoCaptured: 'Auto Captured',
        videoHistory: 'Download Riwayat Video',
        rawAlerts:
          lang === 'en'
            ? this.mediaGroup.rawAlerts[0]?.alert.descriptionEng
            : this.mediaGroup.rawAlerts[0]?.alert.descriptionIdn,
      }
      return mapKey[this.mediaGroup.type]
    },
    thumbnailSrc() {
      if (!this.isVideo) return null
      return this.getThumbnailVideoUrl(this.srcMedia)
    },
    fallbackThumbnailUrl() {
      return require('@/assets/images/thumbnailvideo-media.png')
    },
    calculatedThumbnailSrc() {
      if (!this.isVideo) return null
      if (this.isThumbnailExist) return this.thumbnailSrc
      return this.generatedThumbnailSrc || this.fallbackThumbnailUrl
    },
    calculatedSrcImage() {
      if (!this.isPictureFromVideo) return this.srcMedia
      if (this.isThumbnailExist) return this.getThumbnailVideoUrl(this.srcMedia)
      return this.generatedThumbnailSrc || this.fallbackThumbnailUrl
    },
    mediaCount() {
      return this.mediaGroup.mediaList.length
    },
  },

  watch: {
    async defaultCameraThumbnail(newValue) {
      this.isThumbnailExist = true
      this.refreshThumbnail = true
      await this.delay(250)
      this.refreshThumbnail = false
    },
  },

  methods: {
    async handleThumbnailError() {
      this.isThumbnailExist = false

      if (this.generatedThumbnailSrc || this.isGeneratingThumbnail) return

      this.isGeneratingThumbnail = true
      try {
        const previewUrl = await this.generatePictureUrlFromVideo(this.srcMedia, {
          fetchAsBlob: true,
        })

        if (previewUrl) {
          this.generatedThumbnailSrc = previewUrl
        }
      } finally {
        this.isGeneratingThumbnail = false
      }
    },
    handleClickMediaItem() {
      this.$emit('openModal')
    },
  },
}
</script>
